/**
 * Shared parser/renderer for the in-band `key|paramsJson` message convention (AB#1524).
 *
 * Classified failures written to `BackgroundJobLog.errorMessage` and (AB#1624)
 * `EfaturaOutboundLog.errorMessage` are persisted as `key|json` — a translation key, a literal
 * `|`, then a JSON params object. AB#1365 moved Notification off this in-band packing onto
 * dedicated TitleKey/BodyKey/ParamsJson columns; these two errorMessage fields have no such column
 * split, so they keep the in-band format and this module is their single decoder (extracted out of
 * JobsTab so JobsTab and the e-Fatura log views share one implementation rather than copies).
 *
 * A param value may itself be a keyed token (a job summary line wraps a per-item reason that was
 * classified at its source, e.g. `job.errorSummary.reason|{"reason":"job.error.x|{}"}`). Those are
 * localized before the outer key is resolved, up to a small depth; any other string stays verbatim.
 */

/**
 * Parses one `key|json` token. Returns null when the value isn't well-formed (a raw exception
 * message, or a pre-AB#1524 unclassified row), so the caller can fall back to rendering it verbatim.
 */
export function parseKeyedMessage(
  rawMessage: string,
): { key: string; params: Record<string, unknown> } | null {
  const sep = rawMessage.indexOf('|');
  if (sep <= 0) return null;
  const key = rawMessage.slice(0, sep);
  const jsonRaw = rawMessage.slice(sep + 1);
  try {
    const params = JSON.parse(jsonRaw) as Record<string, unknown>;
    return { key, params };
  } catch {
    return null;
  }
}

/**
 * Renders a possibly multi-line `key|json` encoded message through i18n, falling back to the raw
 * value when it isn't well-formed.
 *
 * AB#1543: a Completed import can carry more than one classified signal (e.g. an axisless variant
 * group AND images skipped for missing storage), newline-joined. Localize every line when all of
 * them are well-formed key|json tokens; if ANY line is not (a raw provider/exception message, or a
 * multi-line exception dump), render the whole value verbatim so unkeyable content still displays.
 */
export function renderKeyedMessage(
  rawMessage: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const lines = rawMessage.split('\n');
  const parsedLines = lines.map((line) => ({ line, parsed: parseKeyedMessage(line) }));
  if (parsedLines.every((p) => p.parsed !== null)) {
    return parsedLines
      .map((p) =>
        t(p.parsed!.key, { ...resolveNestedParams(p.parsed!.params, t, 0), defaultValue: p.line }),
      )
      .join('\n');
  }
  return rawMessage;
}

const MAX_NESTED_DEPTH = 2;

/**
 * Stricter than the top-level parse on purpose: a free-text provider reason may contain a `|`, so a
 * nested value only counts as a token when its key is whitespace-free and its params are a plain
 * object. Anything else must reach the outer translation unchanged.
 */
function parseNestedToken(value: string): { key: string; params: Record<string, unknown> } | null {
  const parsed = parseKeyedMessage(value);
  if (!parsed || /\s/.test(parsed.key)) return null;
  const { params } = parsed;
  if (params === null || typeof params !== 'object' || Array.isArray(params)) return null;
  return parsed;
}

function resolveNestedParams(
  params: Record<string, unknown>,
  t: (key: string, opts?: Record<string, unknown>) => string,
  depth: number,
): Record<string, unknown> {
  // The top-level parse accepts any JSON value (`x|null`, `x|5`); only an object has params to walk.
  if (depth >= MAX_NESTED_DEPTH || params === null || typeof params !== 'object') return params;
  const resolved: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(params)) {
    const nested = typeof value === 'string' ? parseNestedToken(value) : null;
    resolved[name] = nested
      ? t(nested.key, {
          ...resolveNestedParams(nested.params, t, depth + 1),
          defaultValue: value,
        })
      : value;
  }
  return resolved;
}
